import * as yup from "yup";
import { locationFieldsSchema } from "../utils/locationFieldsValidation";

export const newVendorRequestSchema = yup
  .object()
  .shape({
    // General vendor information - always required
    providerName: yup.string().required("Vendor name is required"),
    providerUrl: yup
      .string()
      .required("Provider URL is required")
      .test(
        "is-https",
        "Only HTTPS URLs are accepted for security compliance.",
        (value) => {
          if (!value || typeof value !== "string") return false;
          const v = value.trim().toLowerCase();
          if (v.startsWith("http://")) return false;
          if (v.startsWith("https://")) return true;
          try {
            new URL("https://" + value);
            return true;
          } catch {
            return false;
          }
        }
      ),

    // Primary location - always required (LocationFields: address, city, state if visible, country)
    primaryLocationTextBox: yup
      .string(),
    ...locationFieldsSchema("primary", { mode: "always" }),
    secondaryLocationTextBox: yup.string(),
    country: yup.object().nullable(),
    streetAddress: yup.string(),
    city: yup.string(),
    state: yup.string(),
    zipCode: yup.string(),
    additionalAddress: yup.string(),

    // RFI section - conditionally required
    selectedProvider: yup.mixed().when("$showRfiSection", {
      is: true,
      then: () => yup.mixed(),
    }),
    questionnaireTemplates: yup.array().when("$showRfiSection", {
      is: true,
      then: () =>
        yup
          .array()
          .required("Questionnaire templates are required")
          .min(1, "At least one questionnaire template must be selected"),
    }),
    expectedDate: yup.object().when("$showRfiSection", {
      is: true,
      then: () =>
        yup
          .object({
            startDate: yup
              .date()
              .required("Expected due date is required")
              .test(
                "is-future-date",
                "Due date must be in the future",
                (value) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return value >= today;
                }
              ),
          })
          .required("Expected due date is required"),
    }),
    rfiAdditionalDetails: yup.string().when("$showRfiSection", {
      is: true,
      then: () =>
        yup
          .string()
          .max(2000, "Additional details cannot exceed 2000 characters"),
    }),

    // Assessment section - conditionally required
    locationType: yup.string().when("$showAssessmentSection", {
      is: true,
      then: () =>
        yup
          .string()
          .oneOf(
            ["primary", "secondary", "other"],
            "You must select a location type"
          )
          .required("Location type is required"),
    }),
    otheryLocationTextBox: yup
      .string()
      .when(["locationType", "$showAssessmentSection"], {
        is: (locationType, showAssessmentSection) =>
          locationType === "other" && showAssessmentSection,
        then: () => yup.string().required("Other location address is required"),
      }),
    providerContactDetails: yup.object().when("$showAssessmentSection", {
      is: true,
      then: () => yup.object().nullable(),
    }),
    serviceCategories: yup.array().when("$showAssessmentSection", {
      is: true,
      then: () =>
        yup
          .array()
          .min(1, "Please select at least one service category")
          .required("Service categories are required"),
    }),
    onsiteRemote: yup.object().when("$showAssessmentSection", {
      is: true,
      then: () =>
        yup
          .object()
          .shape({
            onsite: yup.boolean().required("Onsite is required"),
            remote: yup.boolean().required("Remote is required"),
          })
          .test(
            "only-one-true",
            "Only one option (onsite or remote) must be selected",
            (value) => {
              const values = Object.values(value || {});
              return values.filter((v) => v === true).length === 1;
            }
          )
          .required("This field is required"),
    }),
    draftDate: yup.object().when("$showAssessmentSection", {
      is: true,
      then: () =>
        yup
          .object({
            startDate: yup
              .date()
              .required("Desired Initial Draft Date is required")
              .test(
                "is-valid-date",
                "Start date must be a valid date",
                (value) => !isNaN(new Date(value).getTime())
              )
              .test(
                "is-future-date",
                "Draft date must be at least two weeks in the future",
                (value) => {
                  const twoWeeksFromNow = new Date();
                  twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
                  twoWeeksFromNow.setHours(0, 0, 0, 0);
                  return value >= twoWeeksFromNow;
                }
              ),
          })
          .required("Draft date is required"),
    }),
    additionalDetails: yup.string().when("$showAssessmentSection", {
      is: true,
      then: () =>
        yup
          .string()
          .max(2000, "Additional details cannot exceed 2000 characters"),
    }),
  });

// Optional hook for submit handlers that need a one-shot flag on the submitted values object.
export const withSubmitFlag = (onSubmit) => (data) => {
  data._formIsSubmitting = true;
  return onSubmit(data);
};
